<script>
	import thumb_SdB7Oj1Ayv4 from '$lib/Components/Youtube/images/SdB7Oj1Ayv4.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import karenImage from './images/KAREN-vintage-400x320.png';
	import logos from './images/logos.jpg';
	import soundproofingWindowImage from './images/resources-soundproofing-windows-B-001.jpg';
	import interiorSoundReductionImage from './images/resources-soundproofing-windows-B-002.jpg';
	import soundAbsorption from './images/sound-absorption-windows.jpg';
	import soundReductionImage from './images/sound-reduction-windows.jpg';
</script>

<Section backgroundColor="white">
	<p>
		Soundproofing windows depends on how sound is coming into your home and what
		noise is disturbing you. Soundproof windows work against traffic noise,
		barking dogs, & noisy neighbors – and they do this with window insulation. <a
			href="/solutions/window-noise-reduction">Soundproofing window inserts</a
		> provide inside window insulation that both reduces and absorbs sound. Find out
		if they'll work for you:
	</p>

	<Image
		src={soundproofingWindowImage}
		alt="Simple soundproofing window tricks by blocking noise from entering."
	/>
	<Bubble backgroundColor="transparent">
		<nav>
			<ol class="list-decimal flex-col">
				<li>
					<a href="#types-of-soundproofing">Types of Window Soundproofing</a>
				</li>
				<li>
					<a href="#soundproofing-with-inserts"
						>How to Soundproof a Window with Inserts</a
					>
				</li>
				<!-- <li> -->
				<!-- TODO: '#noise-cancellation' section does not exist? -->
				<!-- <a href="#noise-cancellation">Noise Cancelling Window Treatments</a> -->
				<!-- </li> -->
				<li>
					<a href="#sound-absorbing-treatments">Sound Absorbing Treatments</a>
				</li>
			</ol>
		</nav>
	</Bubble>
</Section>

<Section backgroundColor="white">
	<Grid columns={2} rows={2}>
		<Grid subgrid vertical>
			<p class="text-lg font-bold text-indow-grey-dark">Sound Reduction</p>
			<Image
				src={soundReductionImage}
				alt="shows graphically how reduction occur"
			/>
		</Grid>
		<Grid subgrid vertical>
			<p class="text-lg font-bold text-indow-grey-dark">Sound Absorption</p>
			<Image
				src={soundAbsorption}
				alt="shows graphically how absorption takes place inside an indow window"
			/>
		</Grid>
	</Grid>
</Section>

<Section backgroundColor="white" id="types-of-soundproofing">
	<h2>Types of Window Soundproofing</h2>
	<p>
		<strong>Sound Reduction </strong>
		— Decreasing noise from its original level by blocking it. Sound-reducing products
		prevent noise from entering a space.
	</p>

	<p>
		<strong>Sound Absorption </strong>
		— Decreasing noise from its original level by stopping its movement within a space.
		Sound-absorbing products stop noise from bouncing off surfaces.
	</p>

	<div
		class="
    w-full
    max-sm:col-span-full!
  "
	>
		<table class="mb-6 w-full border-collapse text-left">
			<thead class="bg-indow-blue text-white">
				<tr>
					<th class="px-2 py-4 text-base font-bold"
						>How to Increase Sound Reduction</th
					>
					<th class="px-2 py-4 text-base font-bold"
						>How to Increase Sound Absorption</th
					>
				</tr>
			</thead>

			<tbody class="divide-y divide-indow-grey text-indow-grey-dark">
				<tr class="bg-white">
					<td class="px-2 py-4">Seal gaps in window (caulk, weatherstrips)</td>
					<td class="px-2 py-4">Add furniture</td>
				</tr>
				<tr class="bg-indow-grey-light">
					<td class="px-2 py-4">Sound-dampening curtains</td>
					<td class="px-2 py-4">Hang acoustic panels</td>
				</tr>
				<tr class="bg-white">
					<td class="px-2 py-4">Install sound-reducing blinds</td>
					<td class="px-2 py-4">Lay plush rugs</td>
				</tr>
				<tr class="bg-indow-grey-light">
					<td class="px-2 py-4">Install interior storm window inserts</td>
					<td class="px-2 py-4">Purchase plants</td>
				</tr>
			</tbody>
		</table>
	</div>
	<p>
		Now that you know the difference, decide whether you need sound reducing or
		sound absorbing products.<br />
		Don't know which product you need? Noise generated outside calls for sound reducing
		products to stop noise from traveling through windows, ceilings, doors, and walls.
		Noise generated inside requires sound absorption products to reduce echo.
	</p>
	<p>
		If outside noise is your issue, Indow is here for you. Noise travels via
		vibrations in the air and insulating, air sealing inserts block that noise
		from entering your space. Can't decide? You may need both.
	</p>
	<p>
		Indow products can be used to block outside noise from entering while sound
		absorbing materials inside can minimize reverberation. Keep in mind, sound
		reducing products, like inserts, will provide a more noticeable difference
		in noise than sound absorbing products.
	</p>
	<p>
		Learn more about the <a
			href="/resources/blog/most-effective-soundproofing-materials"
			>two types of soundproofing and soundproofing materials</a
		>
	</p>
	<Image
		src={interiorSoundReductionImage}
		alt="Diagram of a soundproof window with original glass and acrylic acoustic insert."
	/>
</Section>

<Section backgroundColor="white" id="soundproofing-with-inserts">
	<h2>How to Soundproof a Window with Inserts</h2>
	<p>
		Installing window inserts is an easy and effective way of soundproofing
		windows. Indow Acoustic Grade window inserts can <a
			href="/solutions/window-noise-reduction">reduce and cancel noise</a
		>
		– up to 70% of outside noise when placed over single-pane windows and up to 50%
		of noise coming through double-pane windows. In
		<a href="/resources/case-studies/office-soundproofing">an office</a>
		or home, soundproofing windows is easy with our inserts that press into your existing
		window frame—no hassle, nails, or adhesives required. But
		<a href="/products/window-insert-cost"
			>how much does it cost to soundproof windows</a
		>?
	</p>
	<Button href="/how-to/get-indow-inserts">GET A FREE ESTIMATE</Button>
	<Image
		src={karenImage}
		alt="Woman installing Indow insert for the simple soundproofing in windows."
	/>
	<p>
		Indow inserts are edged with silicone compression tubing to create a seal
		for your soundproof window. Getting an exact fit is essential in
		soundproofing a window, since gaps and spaces allow sound to seep inside.
		This precise fit also creates a more comfortable, energy efficient space
		while the low profile practically disappears into your window frame. Our
		custom-made inserts offer simple soundproofing for <a
			href="/solutions/odd-shaped-window-inserts">any window shape</a
		>.
	</p>
	<p>Watch this 2 minute demo by window expert, The Craftsman Blog:</p>
	<Youtube posterImageSrc={thumb_SdB7Oj1Ayv4} videoId="SdB7Oj1Ayv4" />
	<p>
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
</Section>

<Section backgroundColor="white" id="sound-absorbing-treatments">
	<h2>Sound Absorbing Treatments</h2>
	<p>
		Despite your best <a href="/resources/home-soundproofing"
			>room soundproofing efforts</a
		>, the sound that does penetrate can be absorbed once it's in the room to
		minimize reverberation. It's best to avoid minimalist style of exposed brick
		and wood floors. Consider hanging
		<a
			href="https://www.audimute.com/soundproofing-acoustical-products"
			target="_blank"
			rel="noopener noreferrer">Acoustic panels</a
		>. Also, plush rugs with sound dampening rug pads, wall hangings,
		upholstered furniture and plants will help absorb sound.
	</p>
</Section>

<Section backgroundColor="grey-light">
	<h2>Indow Inserts Are Featured In:</h2>
	<Image
		src={logos}
		alt="Indow inserts are featured in: The New York Times, Wired, Popular Mechanics, Bloomberg, Treehugger, Sunset, This Old House, Dwell, KGW8"
	/>
	<p>
		“Indow significantly improves insulation and leakage, as well as noise
		reduction.”
	</p>
	<p>
		-RD Gentzler, Architect <br />
		<a
			href="https://www.housebeautiful.com/home-remodeling/diy-projects/a36790852/how-to-soundproof-windows/"
			target="_blank"
			rel="noopener noreferrer"
		>
			<em
				>House Beautiful, How to Soundproof Your Windows Without Actually
				Renovating</em
			>
		</a>
	</p>
</Section>
<Reviews />
<GetAFreeEstimate title="Let Us Help You" />
